/* =========================================================
   Scroll-to-top + right-side floating action stack
   1) Standalone: keeps the whole right-side action stack
      (scroll-to-top, Smile rewards, ChatWill) above any visible
      bottom sticky bar. Sets `--wt-side-stack-lift` on :root
      (or 0 when the merchant disabled the lift behaviour).
      Also mirrors the button's horizontal offset into
      `--wt-side-right` so sibling widgets stay aligned.
   2) The <scroll-to-top> custom element itself.
   ========================================================= */
(function () {
  const root = document.documentElement;
  const BOTTOM_BARS = '.fp-sticky, .wt-product__sticky-buy';
  let ticking = false;
  let trailing;

  function visibleBarHeight() {
    let height = 0;
    document.querySelectorAll(BOTTOM_BARS).forEach((bar) => {
      const rect = bar.getBoundingClientRect();
      if (rect.height <= 0) return;
      // Only bars pinned to the viewport bottom can cover the stack.
      if (window.innerHeight - rect.bottom > 4) return;
      const cs = getComputedStyle(bar);
      if (cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return;
      height = Math.max(height, rect.height);
    });
    return height;
  }

  function sync() {
    const el = document.querySelector('scroll-to-top');
    const liftEnabled = !el || el.getAttribute('data-stt-lift') !== 'false';
    root.style.setProperty(
      '--wt-side-stack-lift',
      liftEnabled ? Math.round(visibleBarHeight()) + 'px' : '0px',
    );

    if (el) {
      const cs = getComputedStyle(el);
      const isLeft = el.classList.contains('wt-scroll-to-top--left') || root.dir === 'rtl';
      const offset = (isLeft ? cs.left : cs.right).trim();
      root.style.setProperty('--wt-side-right', offset && offset !== 'auto' ? offset : '24px');
    }
  }

  function onViewportChange() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        sync();
      });
    }
    // Trailing pass — the theme's own scroll handlers may toggle the sticky
    // bar AFTER ours on the same event, so re-measure once things settle.
    clearTimeout(trailing);
    trailing = setTimeout(sync, 250);
  }

  window.addEventListener('scroll', onViewportChange, { passive: true });
  window.addEventListener('resize', onViewportChange);
  sync();
})();

if (!customElements.get('scroll-to-top')) {
  const EASINGS = {
    linear: (t) => t,
    ease_out: (t) => 1 - Math.pow(1 - t, 3),
    ease_in_out: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  };

  customElements.define('scroll-to-top', class extends HTMLElement {
    connectedCallback() {
      this._btn = this.querySelector('.wt-scroll-to-top__button');
      this._bundleEl = document.querySelector('.wt-bundle');

      const trigger = parseFloat(this.dataset.sttTriggerValue);
      const duration = parseFloat(this.dataset.sttDuration);
      this._triggerValue = Number.isNaN(trigger) ? 100 : trigger;
      this._triggerUnit = this.dataset.sttTriggerUnit || 'px';
      this._duration = Number.isNaN(duration) ? 500 : duration;
      this._easing = EASINGS[this.dataset.sttEasing] || EASINGS.ease_in_out;

      this._onScroll = this._handleScroll.bind(this);
      this._onResize = this._handleScroll.bind(this);
      this._onClick = this._scrollToTop.bind(this);

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);
      this._btn.addEventListener('click', this._onClick);
      this._handleScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      this._btn.removeEventListener('click', this._onClick);
    }

    _threshold() {
      return this._triggerUnit === 'viewport'
        ? window.innerHeight * (this._triggerValue / 100)
        : this._triggerValue;
    }

    _overlapsBundlePanel() {
      if (!this._bundleEl || window.innerWidth > 899) return false;
      const rect = this._bundleEl.getBoundingClientRect();
      const topEntered = rect.top <= window.innerHeight;
      const bottomClearedFold = window.innerHeight - rect.bottom >= 100;
      return topEntered && !bottomClearedFold;
    }

    _handleScroll() {
      // The stack is lifted above any sticky bar via --wt-side-stack-lift,
      // so the button no longer needs to hide itself behind the sticky buy bar.
      this.classList.toggle(
        'wt-scroll-to-top--visible',
        window.scrollY > this._threshold() && !this._overlapsBundlePanel(),
      );
    }

    _scrollToTop() {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const start = window.pageYOffset || document.documentElement.scrollTop || 0;

      // Reduced motion or "browser default" (duration 0): delegate to native smooth scroll.
      if (reducedMotion || this._duration <= 0 || start <= 0) {
        window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
        return;
      }

      let startTime = null;
      const step = (now) => {
        if (startTime === null) startTime = now;
        const progress = Math.min((now - startTime) / this._duration, 1);
        window.scrollTo(0, Math.round(start * (1 - this._easing(progress))));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  });
}
